import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, ArrowLeft, Building2, Mail, Phone, User, Lock, CheckCircle2, ShieldAlert, CreditCard } from "lucide-react";
import staioIcon from "@/assets/images/staio-icon.png";
import { Redirect, Link, useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const registerSchema = z.object({
  fullName: z.string().min(2, "Full name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().optional(),
  companyName: z.string().min(2, "Company name is required"),
  username: z.string().min(3, "Username must be at least 3 characters"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const { user, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [redirectingToStripe, setRedirectingToStripe] = useState(false);

  const { data: regStatus, isLoading: regStatusLoading } = useQuery<{ open: boolean }>({
    queryKey: ["/api/platform/registration-status"],
  });

  const form = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: { fullName: "", email: "", phone: "", companyName: "", username: "", password: "", confirmPassword: "" },
  });

  const registerMutation = useMutation({
    mutationFn: async (values: RegisterForm) => {
      const res = await apiRequest("POST", "/api/register", {
        fullName: values.fullName,
        email: values.email,
        phone: values.phone,
        companyName: values.companyName,
        username: values.username,
        password: values.password,
      });
      const newUser = await res.json();
      return { newUser, companyName: values.companyName, email: values.email };
    },
    onSuccess: async ({ newUser, companyName, email }) => {
      await queryClient.invalidateQueries({ queryKey: ["/api/user"] });

      // Attempt to create a Stripe checkout session for the new org
      if (newUser?.organizationId) {
        setRedirectingToStripe(true);
        try {
          const checkoutRes = await apiRequest("POST", "/api/stripe/create-checkout", {
            orgId: newUser.organizationId,
            email,
            orgName: companyName,
            orgType: "property_management_org",
          });
          if (checkoutRes.ok) {
            const { url } = await checkoutRes.json();
            if (url) {
              window.location.href = url;
              return;
            }
          }
        } catch (err) {
          console.error("[Stripe Checkout] Error creating checkout session:", err);
        }
        // Checkout failed — user account was created but payment not collected.
        // Redirect to payment-required page; the org status guard will keep them there
        // until they complete checkout.
        setRedirectingToStripe(false);
        setLocation("/payment-required");
      }
    },
    onError: (error: Error) => {
      form.setError("root", { message: error.message });
    },
  });

  if (isLoading || regStatusLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  // If redirecting to Stripe, show a loading screen instead of the redirect
  if (redirectingToStripe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(35 40% 95%) 50%, hsl(25 30% 94%) 100%)" }}>
        <img src={staioIcon} alt="Staio" className="w-16 h-16" />
        <div className="text-center space-y-2">
          <CreditCard className="w-8 h-8 mx-auto text-primary animate-pulse" />
          <p className="text-lg font-semibold text-foreground">Redirecting to payment...</p>
          <p className="text-sm text-muted-foreground">Setting up your Staio subscription</p>
        </div>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (user) {
    return <Redirect to="/admin" />;
  }

  if (regStatus && !regStatus.open) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(35 40% 95%) 50%, hsl(25 30% 94%) 100%)" }}>
        <Card className="w-full max-w-md border-t-4 border-t-transparent relative overflow-hidden" style={{ borderImage: "linear-gradient(to right, #f59e0b, #ea580c) 1" }}>
          <CardHeader className="text-center space-y-3">
            <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mx-auto">
              <ShieldAlert className="w-8 h-8 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-2xl font-display font-bold" data-testid="text-register-closed-title">Registration Closed</CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                New account registration is currently unavailable. Please contact the platform administrator if you'd like to get started.
              </p>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Link href="/auth" data-testid="link-register-login">
              <Button variant="outline" className="w-full">Already have an account? Log in</Button>
            </Link>
            <Link href="/" data-testid="link-register-back-home">
              <Button variant="ghost" className="w-full"><ArrowLeft className="w-4 h-4 mr-2" />Back to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  async function onSubmit(values: RegisterForm) {
    registerMutation.mutate(values);
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, hsl(30 33% 98%) 0%, hsl(35 40% 95%) 50%, hsl(25 30% 94%) 100%)" }}>
      <div className="absolute top-20 left-0 w-96 h-96 bg-gradient-to-br from-orange-200/20 to-amber-200/10 rounded-full blur-3xl -z-10" />
      <Card className="w-full max-w-lg border-t-4 border-t-transparent relative overflow-hidden" style={{ borderImage: "linear-gradient(to right, #f59e0b, #ea580c) 1" }}>
        <CardHeader className="text-center space-y-3">
          <img src={staioIcon} alt="Staio" className="w-14 h-14 mx-auto" />
          <div>
            <CardTitle className="text-2xl font-display font-bold" data-testid="text-register-title">Get Started with Staio</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Create your property management account</p>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input placeholder="Your full name" className="pl-9" {...field} data-testid="input-register-fullname" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input type="email" placeholder="you@company.com" className="pl-9" {...field} data-testid="input-register-email" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input placeholder="Your company" className="pl-9" {...field} data-testid="input-register-company" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone <span className="text-muted-foreground text-xs">(optional)</span></FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input placeholder="+1 (555) 000-0000" className="pl-9" {...field} data-testid="input-register-phone" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="border-t pt-4 mt-2">
                <p className="text-xs text-muted-foreground mb-3">Account Credentials</p>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Username</FormLabel>
                        <FormControl>
                          <Input placeholder="Choose a username" {...field} data-testid="input-register-username" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                              <Input type="password" placeholder="Min 6 characters" className="pl-9" {...field} data-testid="input-register-password" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="Confirm password" {...field} data-testid="input-register-confirm" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              {form.formState.errors.root && (
                <div className="text-sm font-medium text-destructive text-center" data-testid="text-register-error">
                  {form.formState.errors.root.message}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={registerMutation.isPending} data-testid="button-register-submit">
                {registerMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : (
                  <>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Create Account & Set Up Subscription
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-2">
                By signing up, you agree to our Terms of Service and Privacy Policy.
                A 7-day free trial applies — no charge until the trial ends.
              </p>
            </form>
          </Form>
          <div className="mt-4 flex items-center justify-between">
            <Link href="/">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors" data-testid="link-register-back-home">
                <ArrowLeft className="w-3 h-3" />
                Back to home
              </span>
            </Link>
            <Link href="/auth">
              <span className="text-xs text-primary hover:text-primary/80 cursor-pointer transition-colors" data-testid="link-register-login">
                Already have an account? Sign in
              </span>
            </Link>
          </div>
          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-xs text-muted-foreground">
              Need help? Contact us at{" "}
              <a href="mailto:info@staio.io" className="text-primary hover:underline">info@staio.io</a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
